import { Plugin, PluginKey } from 'hubspot-prosemirror/pm/state';
import { Decoration, DecorationSet } from 'hubspot-prosemirror/pm/view';
import ReactDOM from 'react-dom';
import uuid from 'hubspot-prosemirror/prosemirror/utils/uuid';
import { insertPersonalizationTokenAtRange } from 'hubspot-prosemirror/prosemirror/utils/documentUtils';
import PersonalizationTokenInlineDropdown from './PersonalizationTokenInlineDropdown';
import { jsx as _jsx } from "react/jsx-runtime";
export function initPluginState() {
  return {
    active: false,
    triggerPos: 0,
    searchText: '',
    selectedIndex: 0,
    dismissedPos: null,
    pendingToken: null
  };
}
function computeSearchText(tr, state) {
  if (!state.active) {
    return state;
  }
  const cursorPos = tr.selection.from;
  if (cursorPos < state.triggerPos) {
    return initPluginState();
  }
  const prevSearchEnd = state.triggerPos + state.searchText.length;
  if (cursorPos > prevSearchEnd + 1) {
    return initPluginState();
  }
  const searchText = tr.doc.textBetween(state.triggerPos, cursorPos, '');
  if (searchText.includes('}')) {
    return Object.assign({}, initPluginState(), {
      dismissedPos: state.triggerPos
    });
  }
  if (searchText !== state.searchText) {
    return Object.assign({}, state, {
      searchText,
      selectedIndex: 0
    });
  }
  return state;
}
export function buildDisplayText(token) {
  const baseDisplayText = token.displayText || token.tokenKey;
  return token.tokenGroupLabel ? `${token.tokenGroupLabel}: ${baseDisplayText}` : baseDisplayText;
}
export function createPersonalizationTokenInlinePlugin({
  portalApi,
  fetchTokens,
  onSelectToken,
  defaultValue
} = {}) {
  const showFallbackStep = Boolean(defaultValue);
  const uniqueKey = uuid();
  const pluginKey = new PluginKey(`personalizationTokenInlinePlugin_${uniqueKey}`);
  const pendingTokens = {
    current: []
  };
  const viewRef = {
    current: null
  };
  let dropdownContainer;
  let dropdownContainerPortal = null;
  function getState(editorState) {
    return pluginKey.getState(editorState);
  }
  function deactivateDismiss(view) {
    const currentState = getState(view.state);
    view.dispatch(view.state.tr.setMeta(pluginKey, Object.assign({}, initPluginState(), {
      dismissedPos: currentState.triggerPos
    })));
  }
  function deactivateSilent(view) {
    const currentState = getState(view.state);
    view.dispatch(view.state.tr.setMeta(pluginKey, Object.assign({}, initPluginState(), {
      dismissedPos: currentState.active ? currentState.triggerPos : null
    })));
  }
  function selectToken(view, token, state) {
    if (showFallbackStep) {
      view.dispatch(view.state.tr.setMeta(pluginKey, Object.assign({}, state, {
        pendingToken: token
      })));
      return;
    }
    insertTokenDirectly(view, token, state);
  }
  function insertTokenDirectly(view, token, state, defaultTokenValue) {
    const {
      triggerPos,
      searchText
    } = state;
    const from = triggerPos - 2;
    const to = state.pendingToken ? triggerPos + searchText.length : view.state.selection.from;
    const tr = insertPersonalizationTokenAtRange({
      from,
      to,
      tokenKey: token.tokenKey,
      displayText: buildDisplayText(token),
      tokenStyles: token.tokenStyles,
      defaultTokenValue,
      editorState: view.state
    });
    view.dispatch(tr.setMeta(pluginKey, initPluginState()));
    if (onSelectToken) {
      onSelectToken(token, view);
    }
  }
  function confirmToken(fallbackValue) {
    const view = viewRef.current;
    if (!view) return;
    const state = getState(view.state);
    if (!state.pendingToken) return;
    insertTokenDirectly(view, state.pendingToken, state, fallbackValue);
  }
  function cancelPendingToken() {
    const view = viewRef.current;
    if (!view) return;
    const currentState = getState(view.state);
    if (!currentState.pendingToken) return;
    view.dispatch(view.state.tr.setMeta(pluginKey, Object.assign({}, currentState, {
      pendingToken: null,
      selectedIndex: 0
    })));
    view.focus();
  }
  function handleTokenSelect(token) {
    const view = viewRef.current;
    if (!view) {
      return;
    }
    const state = getState(view.state);
    selectToken(view, token, state);
  }
  function setTokens(tokens) {
    pendingTokens.current = tokens;
  }
  const createDropdown = function createDropdown() {
    if (dropdownContainer && dropdownContainerPortal) {
      return dropdownContainer;
    }
    dropdownContainer = document.createElement('span');
    dropdownContainerPortal = /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsx(PersonalizationTokenInlineDropdown, {
      pluginKey: pluginKey,
      fetchTokens: fetchTokens,
      onSelect: handleTokenSelect,
      onTokensChange: setTokens,
      onConfirmToken: confirmToken,
      onCancelPendingToken: cancelPendingToken,
      defaultValueConfig: defaultValue
    }), dropdownContainer, uniqueKey);
    portalApi.createPortal(dropdownContainerPortal);
    return dropdownContainer;
  };
  const closeDropdown = () => {
    if (dropdownContainerPortal && portalApi) {
      portalApi.destroyPortal(dropdownContainerPortal.key);
      dropdownContainerPortal = null;
      dropdownContainer = undefined;
      pendingTokens.current = [];
    }
  };
  return {
    pluginKey,
    selectToken,
    confirmToken,
    cancelPendingToken,
    _testSetTokens: setTokens,
    plugin: new Plugin({
      key: pluginKey,
      state: {
        init() {
          return initPluginState();
        },
        apply(tr, value) {
          const meta = tr.getMeta(pluginKey);
          if (meta) {
            return meta;
          }
          if (!value.active) {
            return value;
          }
          if (value.pendingToken) {
            return value;
          }
          return computeSearchText(tr, value);
        }
      },
      appendTransaction(_transactions, _oldState, newState) {
        const currentState = getState(newState);
        if (currentState.active) {
          return null;
        }
        const {
          $from
        } = newState.selection;
        const pos = $from.pos;
        if (currentState.dismissedPos !== null) {
          const dp = currentState.dismissedPos;
          const triggerStillExists = dp >= 3 && dp <= newState.doc.content.size && newState.doc.textBetween(dp - 2, dp, '') === '{{';
          if (!triggerStillExists) {
            return newState.tr.setMeta(pluginKey, initPluginState());
          }
        }
        if (pos < 3) {
          return null;
        }
        const textBefore = newState.doc.textBetween(pos - 2, pos, '');
        if (textBefore === '{{') {
          if (currentState.dismissedPos === pos) {
            return null;
          }
          return newState.tr.setMeta(pluginKey, Object.assign({}, initPluginState(), {
            active: true,
            triggerPos: pos,
            selectedIndex: 0
          }));
        }
        return null;
      },
      props: {
        handleClick(view, __pos, __event) {
          const state = getState(view.state);
          if (state.active && state.pendingToken) {
            cancelPendingToken();
            return false;
          }
          if (state.active) {
            const cursorPos = view.state.selection.from;
            if (cursorPos < state.triggerPos) {
              deactivateSilent(view);
            }
          }
          return false;
        },
        handleDOMEvents: {
          blur(view) {
            const state = getState(view.state);
            if (state.active && !state.pendingToken) {
              deactivateSilent(view);
            }
            return false;
          }
        },
        handleKeyDown(view, e) {
          const state = getState(view.state);
          if (!state.active) {
            return false;
          }
          if (state.pendingToken) {
            return false;
          }
          const tr = view.state.tr;
          switch (e.key) {
            case 'ArrowUp':
              {
                view.dispatch(tr.setMeta(pluginKey, Object.assign({}, state, {
                  selectedIndex: Math.max(0, state.selectedIndex - 1)
                })));
                return true;
              }
            case 'ArrowDown':
              {
                const maxIndex = Math.max(0, pendingTokens.current.length - 1);
                view.dispatch(tr.setMeta(pluginKey, Object.assign({}, state, {
                  selectedIndex: Math.min(state.selectedIndex + 1, maxIndex)
                })));
                return true;
              }
            case 'Enter':
              {
                const tokens = pendingTokens.current;
                if (tokens.length === 0) {
                  deactivateSilent(view);
                  return true;
                }
                const idx = Math.min(state.selectedIndex, tokens.length - 1);
                selectToken(view, tokens[idx], state);
                return true;
              }
            case 'Escape':
              {
                deactivateDismiss(view);
                return true;
              }
            case 'Backspace':
              {
                const cursorPos = view.state.selection.from;
                if (cursorPos <= state.triggerPos) {
                  deactivateSilent(view);
                }
                return false;
              }
            default:
              return false;
          }
        },
        decorations(editorState) {
          const pluginState = getState(editorState);
          if (!pluginState.active || !portalApi || !fetchTokens) {
            closeDropdown();
            return null;
          }
          return DecorationSet.create(editorState.doc, [Decoration.widget(pluginState.triggerPos, createDropdown, {
            side: 0
          })]);
        }
      },
      view(editorView) {
        viewRef.current = editorView;
        return {
          update: (updatedView, prevState) => {
            viewRef.current = updatedView;
            const prev = getState(prevState);
            const next = getState(updatedView.state);
            if (prev.active && !next.active) {
              closeDropdown();
            }
          },
          destroy: () => {
            viewRef.current = null;
            const containerToRemove = dropdownContainer;
            closeDropdown();
            if (containerToRemove) {
              containerToRemove.remove();
            }
          }
        };
      }
    })
  };
}